"use client";

/**
 * Sponsors Renderer
 * Renders a sponsor logo grid with optional tier groupings on the canvas.
 * Uses stagger logic so no row looks lopsided — the last row is always centered.
 */

import type React from "react";
import { useCallback, useMemo } from "react";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { isSafeUrl } from "../../../_logic/utils";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SanitizedHtml } from "./_shared/SanitizedHtml";
import { SectionContainer } from "./_shared/SectionContainer";
import type { SponsorsComponent, SponsorEntry } from "../../../_types";
import { PROSE_SECTION_TEXT_VARS } from "./_shared/prose-section-text";

/** Shared card hover styling — hoisted to module scope to avoid per-render allocation */
const SPONSOR_CARD_HOVER_CLASS =
  "transition-all duration-150 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md";

/**
 * iOS Safari delays scroll detection when a finger lands on an <a> element,
 * waiting to decide between tap, long-press, and pan. Sponsor cards wrap the
 * entire (tall) card in an <a>, so the user's finger almost always lands on
 * the link when scrolling — making the page feel "stuck on the cards".
 * touch-action: manipulation routes the gesture straight to pan/zoom and
 * removes the long-press delay.
 */
const SPONSOR_CARD_TOUCH_CLASS = "touch-manipulation";

interface SponsorsRendererProps {
  component: SponsorsComponent;
}

/**
 * Calculates the optimal number of columns so that no row is lopsided.
 * The last row will always be centered when rendered with justify-content: center.
 *
 * @param n - Number of items
 * @param maxCols - Maximum columns for this breakpoint
 * @returns Optimal column count
 */
function calcOptimalCols(n: number, maxCols: number): number {
  if (n <= 0) return 1;
  const rows = Math.ceil(n / maxCols);
  return Math.ceil(n / rows);
}

interface SponsorGridProps {
  sponsors: SponsorEntry[];
  componentId: string;
  isPublic: boolean;
}

/** Renders a centered stagger grid for a group of sponsors */
function SponsorGrid({ sponsors, componentId, isPublic }: SponsorGridProps): React.ReactElement {
  // Optimal columns at the widest breakpoint (max 4). Narrower container widths step
  // down via container-query breakpoints so cards don't squish on mobile.
  const optimalCols = useMemo(() => calcOptimalCols(sponsors.length, 4), [sponsors.length]);

  // Card width: full-width single column on small screens, then step up to optimalCols
  // as the container grows. max-w-[360px] caps the per-card size at any breakpoint.
  const cardWidthClass = (() => {
    if (optimalCols === 1) return "w-full max-w-[360px]";
    if (optimalCols === 2)
      return `w-full max-w-[360px] ${isPublic ? "sm:w-[calc(50%-0.75rem)]" : "@xs:w-[calc(50%-0.75rem)]"}`;
    if (optimalCols === 3) {
      return `w-full max-w-[360px] ${
        isPublic
          ? "sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)]"
          : "@xs:w-[calc(50%-0.75rem)] @md:w-[calc(33.333%-1rem)]"
      }`;
    }
    return `w-full max-w-[360px] ${
      isPublic
        ? "sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(25%-1.125rem)]"
        : "@xs:w-[calc(50%-0.75rem)] @md:w-[calc(33.333%-1rem)] @xl:w-[calc(25%-1.125rem)]"
    }`;
  })();

  return (
    <div className="flex flex-wrap justify-center gap-4">
      {sponsors.map((sponsor) => (
        <SponsorCard
          key={sponsor.id}
          sponsor={sponsor}
          cardWidthClass={cardWidthClass}
          componentId={componentId}
          isPublic={isPublic}
        />
      ))}
    </div>
  );
}

interface SponsorCardProps {
  sponsor: SponsorEntry;
  cardWidthClass: string;
  componentId: string;
  isPublic: boolean;
}

/** Single sponsor card — logo, name, bio, note badge */
function SponsorCard({ sponsor, cardWidthClass, componentId, isPublic }: SponsorCardProps): React.ReactElement {
  const fieldClick = useCanvasFieldClick(componentId);
  const { primaryColor } = useBuilderContext();
  // Re-validate stored URL at render — defense-in-depth against legacy/imported records
  // that might not have been validated at write time.
  const safeWebsiteUrl = sponsor.websiteUrl && isSafeUrl(sponsor.websiteUrl) ? sponsor.websiteUrl : null;
  const isClickable = isPublic && !!safeWebsiteUrl;

  const handleCanvasClick = useMemo(() => fieldClick(`sponsors-item:${sponsor.id}`), [fieldClick, sponsor.id]);

  const handleCanvasKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCanvasClick(e as unknown as React.MouseEvent);
      }
    },
    [handleCanvasClick]
  );

  const content = (
    <div className="flex flex-col items-center gap-4 p-6">
      {/* Logo */}
      <div className="flex h-28 items-center justify-center">
        {sponsor.logoUrl ? (
          <img src={sponsor.logoUrl} alt={sponsor.name || "Sponsor logo"} className="h-28 max-w-full object-contain" />
        ) : (
          <div className="flex h-28 w-40 items-center justify-center rounded bg-gray-100">
            <span className="text-sm font-medium text-gray-500">Logo</span>
          </div>
        )}
      </div>

      {/*
        Raw `primaryColor`, for the same reason as the team member name in `TeamRenderer` — see the
        comment there. This was `style={{ color: primaryColor }}` and the accent vars are published
        whether or not the section has a background set, so correcting it here would have changed
        the sponsor names on every already-published page.
      */}
      {sponsor.name && (
        <h3 className="text-center text-xl font-semibold" style={{ color: primaryColor }}>
          {sponsor.name}
        </h3>
      )}

      {/*
        This whole card is its own opaque white surface (see `baseCardClass`'s `bg-white` below),
        so its contents stay plain gray regardless of the section's own background — they're never
        actually sitting on the section's color, unlike content that renders directly on it.
      */}
      {/* Bio — full rich text, no clamp so longer descriptions stay readable */}
      {sponsor.bio && (
        <SanitizedHtml
          html={sponsor.bio}
          className="prose prose-gray max-w-none text-center text-base font-medium text-gray-500 prose-a:text-[var(--lp-accent-on-card)] prose-a:underline"
        />
      )}

      {/* Sponsor note badge */}
      {sponsor.sponsorNote && (
        <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm font-medium text-gray-500">
          {sponsor.sponsorNote}
        </span>
      )}
    </div>
  );

  const baseCardClass = `${cardWidthClass} rounded-lg border border-gray-200 bg-white`;

  if (isClickable && safeWebsiteUrl) {
    return (
      <a
        href={safeWebsiteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${baseCardClass} ${SPONSOR_CARD_HOVER_CLASS} ${SPONSOR_CARD_TOUCH_CLASS}`}
        aria-label={`${sponsor.name} — visit website (opens in new tab)`}
      >
        {content}
      </a>
    );
  }

  if (!isPublic) {
    // role="button" rather than <button> because the card contains block-level rich text
    // (bio HTML), which is invalid as a <button> descendant per HTML spec.
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleCanvasClick}
        onKeyDown={handleCanvasKeyDown}
        className={`${baseCardClass} cursor-pointer text-left ${SPONSOR_CARD_HOVER_CLASS} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500`}
        aria-label={`Edit ${sponsor.name || "sponsor"}`}
      >
        {content}
      </div>
    );
  }

  return <div className={baseCardClass}>{content}</div>;
}

/** Renders sponsor section with optional tier groupings */
export function SponsorsRenderer({ component }: SponsorsRendererProps): React.ReactElement {
  const { title, description, showTiers, tierOrder = [], sponsors = [] } = component;
  const { isPublic } = usePublicViewContext();

  // Sponsors are stored in display order (managed via DnD arrayMove in the editor)
  const sortedSponsors = sponsors;

  // Render
  return (
    <SectionContainer
      maxWidth="4xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="sponsors-title"
    >
      <InlineTipTapEdit
        content={description || ""}
        className={`prose prose-base prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline ${PROSE_SECTION_TEXT_VARS}`}
        componentId={component.id}
        fieldId="sponsors-description"
        field="description"
        placeholder="Click to add description..."
      />

      {sortedSponsors.length === 0 ? null : showTiers && tierOrder.length > 0 ? (
        <TieredSponsors
          sponsors={sortedSponsors}
          tierOrder={tierOrder}
          componentId={component.id}
          isPublic={isPublic}
        />
      ) : (
        <SponsorGrid sponsors={sortedSponsors} componentId={component.id} isPublic={isPublic} />
      )}
    </SectionContainer>
  );
}

interface TieredSponsorsProps {
  sponsors: SponsorEntry[];
  tierOrder: string[];
  componentId: string;
  isPublic: boolean;
}

/** Groups and renders sponsors under their tier labels in tier order */
function TieredSponsors({ sponsors, tierOrder, componentId, isPublic }: TieredSponsorsProps): React.ReactElement {
  // Build an ordered list of groups. `label === null` means "untiered" — rendered last,
  // without a heading. Using a structured array (rather than a Map with a sentinel key)
  // avoids collisions if a user names a tier the same as an internal sentinel.
  const groups = useMemo<Array<{ label: string | null; sponsors: SponsorEntry[] }>>(() => {
    const tierMap = new Map<string, SponsorEntry[]>();
    for (const tier of tierOrder) tierMap.set(tier, []);
    const untiered: SponsorEntry[] = [];
    for (const sponsor of sponsors) {
      const bucket = sponsor.tier ? tierMap.get(sponsor.tier) : undefined;
      if (bucket) {
        bucket.push(sponsor);
      } else {
        untiered.push(sponsor);
      }
    }
    const ordered: Array<{ label: string | null; sponsors: SponsorEntry[] }> = [];
    for (const tier of tierOrder) {
      const bucket = tierMap.get(tier);
      if (bucket && bucket.length > 0) ordered.push({ label: tier, sponsors: bucket });
    }
    if (untiered.length > 0) ordered.push({ label: null, sponsors: untiered });
    return ordered;
  }, [sponsors, tierOrder]);

  return (
    <div className="flex flex-col gap-10">
      {groups.map((group, index) => (
        <div key={group.label ?? `__untiered_${index}`} className="flex flex-col gap-4">
          {group.label !== null && (
            <h3 className="text-center text-[0.7rem] font-medium uppercase tracking-widest text-[color:var(--lp-text,#6b7280)]">
              {group.label}
            </h3>
          )}
          <SponsorGrid sponsors={group.sponsors} componentId={componentId} isPublic={isPublic} />
        </div>
      ))}
    </div>
  );
}
